function present(value) {
  return String(value || "").trim();
}

export function calculateAgeFromDob(dob, today = new Date()) {
  const raw = present(dob);
  if (!raw) return null;
  const birthDate = new Date(`${raw.slice(0, 10)}T00:00:00`);
  const asOf = today instanceof Date ? today : new Date(today);
  if (Number.isNaN(birthDate.getTime()) || Number.isNaN(asOf.getTime())) return null;

  let age = asOf.getFullYear() - birthDate.getFullYear();
  const monthDiff = asOf.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

export function isPatientMinor(patient, today = new Date()) {
  const age = calculateAgeFromDob(patient?.date_of_birth, today);
  return age != null && age < 18;
}

export function buildOrthoContractSnapshot({ patient, guardian = {}, issuedAt = new Date() }) {
  const patientAge = calculateAgeFromDob(patient?.date_of_birth, issuedAt);
  const minorAtContract = patientAge != null && patientAge < 18;
  return {
    contract_generated_at: issuedAt instanceof Date ? issuedAt.toISOString() : new Date(issuedAt).toISOString(),
    patient_age_at_contract: patientAge,
    minor_at_contract: minorAtContract,
    guardian_full_name: minorAtContract ? present(guardian.fullName || guardian.guardian_full_name) || null : null,
    guardian_relationship: minorAtContract ? present(guardian.relationship || guardian.guardian_relationship) || null : null,
  };
}

export function getOrthoPlanCreationBlock({ patient }) {
  if (!present(patient?.date_of_birth)) {
    return {
      code: "missing_patient_dob",
      message: "Patient date of birth is required before starting an Ortho case. Open the patient profile and fill in Date of Birth.",
    };
  }
  return null;
}

export function getDoctorProfileForContract(doctor) {
  return {
    fullName: present(doctor?.full_name || doctor?.display_name),
    prcLicense: present(doctor?.prc_license || doctor?.license_no),
  };
}

export function getOrthoDoctorProfileBlock(doctor) {
  const doctorProfile = getDoctorProfileForContract(doctor);
  if (!doctorProfile.fullName) {
    return {
      code: "missing_dentist_name",
      message: "Attending dentist full name is missing. Clinic admin must complete the dentist profile before the Ortho contract can be generated.",
    };
  }
  if (!doctorProfile.prcLicense) {
    return {
      code: "missing_dentist_prc",
      message: "Attending dentist PRC License Number is missing. Clinic admin must complete the dentist profile before the Ortho contract can be generated.",
    };
  }
  return null;
}

export function getOrthoContractBlock({
  patient,
  doctor,
  guardian = {},
  today = new Date(),
}) {
  const patientBlock = getOrthoPlanCreationBlock({ patient });
  if (patientBlock) return patientBlock;

  const doctorBlock = getOrthoDoctorProfileBlock(doctor);
  if (doctorBlock) return doctorBlock;

  const snapshot = buildOrthoContractSnapshot({ patient, guardian, issuedAt: today });
  if (snapshot.minor_at_contract) {
    if (!present(snapshot.guardian_full_name)) {
      return {
        code: "missing_guardian_full_name",
        message: "Guardian full name is required before generating the Ortho contract.",
      };
    }
    if (!present(snapshot.guardian_relationship)) {
      return {
        code: "missing_guardian_relationship",
        message: "Guardian relationship to patient is required before generating the Ortho contract.",
      };
    }
  }

  return null;
}

export function getOrthoVisitStartBlock({ row, visitType }) {
  const visits = Array.isArray(row?.visits)
    ? row.visits.filter((visitRow) => (
      !visitRow?.ortho_visit?.voided_at
      && visitRow?.procedure_instance?.status !== "voided"
      && visitRow?.procedure_visit?.outcome !== "voided"
    ))
    : [];
  const isFirstVisit = visits.length === 0 || String(visitType || "").toLowerCase() === "bonding";
  if (isFirstVisit && !present(row?.billing?.contract_signed_at)) {
    return {
      code: "missing_signed_contract",
      message: "A signed Ortho contract must be on record before the first Ortho visit can be started. Mark the contract signed on the Ortho case first.",
    };
  }
  return null;
}
